/**
 * Session summariser policy, status, health, requalification plan, browse and
 * regenerate endpoints.
 *
 * Every path is slash-less, mirroring the backend router declarations
 * byte-for-byte — FastAPI 307-redirects a stray trailing slash before auth, so
 * the client would follow an unauthenticated redirect and receive a 401.
 *
 * There is deliberately no by-session summary fetch: the timeline route carries
 * the session's register row beside its sections, so the detail panel reads its
 * headline from the page it already fetches. The archive panel reads its row
 * from the archive list, which never changes under it.
 */

import type {
  ArchivedSummariesParams,
  SessionSummaryArchiveListResponse,
  SessionSummaryVersionListResponse,
} from '@/types/sessionSummaryArchive'
import type {
  RegenerateAccepted,
  RegenerateStatus,
  SummarizationHealth,
  SummarizationPolicy,
  SummarizationPolicyUpdate,
  SummarizationStatus,
  SummaryRangeAnalysisAccepted,
  SummaryRangeAnalysisRequest,
  SummaryRangeAnalysisStatus,
  VersionListParams,
} from '@/types/summarization'
import type { SummarizationRequalificationPlan } from '@/types/summarizationRequalification'
import type {
  SessionSummaryRegisterParams,
  SessionSummaryRowListResponse,
} from '@/types/sessionSummaryRegister'
import type {
  ChunkSection,
  ChunkVersionsResponse,
  SessionSummaryTimelineResponse,
  SummaryTimelineParams,
} from '@/types/sessionSummaryTimeline'
import { apiClient } from './client'
import { repeatedKeyParamsSerializer } from './queryParams'
import { optionalIfMatchHeaders } from './ifMatch'

export const summarization = {
  getPolicy: async (): Promise<SummarizationPolicy> => {
    const response = await apiClient.get<SummarizationPolicy>(
      '/api/v1/summarization/policy'
    )
    return response.data
  },

  // ifMatch is the current policy_version; omit for last-write-wins semantics.
  // A mismatch returns 412 — surfaced by the 412 branch of handleMutationError.
  updatePolicy: async (
    body: SummarizationPolicyUpdate,
    ifMatch?: number
  ): Promise<SummarizationPolicy> => {
    const response = await apiClient.put<SummarizationPolicy>(
      '/api/v1/summarization/policy',
      body,
      optionalIfMatchHeaders(ifMatch)
    )
    return response.data
  },

  getStatus: async (): Promise<SummarizationStatus> => {
    const response = await apiClient.get<SummarizationStatus>(
      '/api/v1/summarization/status'
    )
    return response.data
  },

  getHealth: async (): Promise<SummarizationHealth> => {
    const response = await apiClient.get<SummarizationHealth>(
      '/api/v1/summarization/health'
    )
    return response.data
  },

  // Operator only: a Viewer is refused with 403. It still answers with model
  // evaluation switched off, each step that would launch then reading as
  // blocked on that switch.
  getRequalificationPlan: async (): Promise<SummarizationRequalificationPlan> => {
    const response = await apiClient.get<SummarizationRequalificationPlan>(
      '/api/v1/summarization/requalification'
    )
    return response.data
  },

  getRecentSummaries: async (
    params: SessionSummaryRegisterParams = {}
  ): Promise<SessionSummaryRowListResponse> => {
    // `status` repeats its key for each value, the way FastAPI reads a list.
    const response = await apiClient.get<SessionSummaryRowListResponse>(
      '/api/v1/activities/summaries/recent',
      { params, paramsSerializer: repeatedKeyParamsSerializer }
    )
    return response.data
  },

  getArchivedSummaries: async (
    params: ArchivedSummariesParams = {}
  ): Promise<SessionSummaryArchiveListResponse> => {
    const response = await apiClient.get<SessionSummaryArchiveListResponse>(
      '/api/v1/activities/summaries/archive',
      { params }
    )
    return response.data
  },

  getSummaryVersions: async (
    sessionId: string,
    params: VersionListParams = {}
  ): Promise<SessionSummaryVersionListResponse> => {
    const response = await apiClient.get<SessionSummaryVersionListResponse>(
      `/api/v1/activities/sessions/${sessionId}/summary/versions`,
      { params }
    )
    return response.data
  },

  // 202 — the outcome is read on the handle this answers with, through
  // getRegenerateStatus. The body is empty: the backend refuses any field.
  regenerate: async (sessionId: string): Promise<RegenerateAccepted> => {
    const response = await apiClient.post<RegenerateAccepted>(
      `/api/v1/activities/sessions/${sessionId}/summary/regenerate`,
      {}
    )
    return response.data
  },

  // 202 — the analysis is brokered to a relay; the result arrives by polling
  // getRangeAnalysis and is never persisted.
  analyzeRange: async (
    sessionId: string,
    body: SummaryRangeAnalysisRequest
  ): Promise<SummaryRangeAnalysisAccepted> => {
    const response = await apiClient.post<SummaryRangeAnalysisAccepted>(
      `/api/v1/activities/sessions/${sessionId}/summary/range-analysis`,
      body
    )
    return response.data
  },

  // The rolling chunk timeline plus the whole-session convergence and
  // overview blocks in one read, so a page never assembles those from two
  // responses that can disagree.
  getSessionTimeline: async (
    sessionId: string,
    params: SummaryTimelineParams = {}
  ): Promise<SessionSummaryTimelineResponse> => {
    const response = await apiClient.get<SessionSummaryTimelineResponse>(
      `/api/v1/activities/sessions/${sessionId}/summary/timeline`,
      {
        params: {
          limit: params.limit,
          offset: params.offset,
          include_superseded: params.includeSuperseded,
        },
      }
    )
    return response.data
  },

  // Compound-scoped: the chunk is addressed under its session, so a chunk id
  // from another session is a 404 rather than a cross-session read.
  getChunkVersions: async (
    sessionId: string,
    chunkId: string,
    params: VersionListParams = {}
  ): Promise<ChunkVersionsResponse> => {
    const response = await apiClient.get<ChunkVersionsResponse>(
      `/api/v1/activities/sessions/${sessionId}/summary/chunks/${chunkId}/versions`,
      { params }
    )
    return response.data
  },

  // ifMatch is the chunk's current metadata_version; a stale value returns 412
  // and a chunk outside {quarantined, retry_wait} returns 409 naming the state.
  // Both answer with the updated section, never with the fire's outcome — that
  // arrives on session_summary_chunk_changed.
  retryChunk: async (
    sessionId: string,
    chunkId: string,
    ifMatch?: number
  ): Promise<ChunkSection> => {
    const response = await apiClient.post<ChunkSection>(
      `/api/v1/activities/sessions/${sessionId}/summary/chunks/${chunkId}/retry`,
      undefined,
      optionalIfMatchHeaders(ifMatch)
    )
    return response.data
  },

  // Retry without the immediate fire: the chunk returns to the ready queue and
  // the next drain cycle picks it up.
  releaseChunk: async (
    sessionId: string,
    chunkId: string,
    ifMatch?: number
  ): Promise<ChunkSection> => {
    const response = await apiClient.post<ChunkSection>(
      `/api/v1/activities/sessions/${sessionId}/summary/chunks/${chunkId}/release`,
      undefined,
      optionalIfMatchHeaders(ifMatch)
    )
    return response.data
  },

  // One poll of an accepted regenerate, folded from the request's per-chunk
  // bindings. The console follows every request it makes on this handle; the
  // chunk and overview broadcasts invalidate it as sections land.
  getRegenerateStatus: async (
    sessionId: string,
    requestId: string
  ): Promise<RegenerateStatus> => {
    const response = await apiClient.get<RegenerateStatus>(
      `/api/v1/activities/sessions/${sessionId}/summary/regenerate/${requestId}`
    )
    return response.data
  },

  // One lifecycle poll; a done result is delivered exactly once, then the
  // rendezvous forgets the id.
  getRangeAnalysis: async (
    sessionId: string,
    analysisId: string,
    opts: { signal?: AbortSignal } = {}
  ): Promise<SummaryRangeAnalysisStatus> => {
    const response = await apiClient.get<SummaryRangeAnalysisStatus>(
      `/api/v1/activities/sessions/${sessionId}/summary/range-analysis/${analysisId}`,
      { signal: opts.signal }
    )
    return response.data
  },
}
